import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Copy, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = "texoma_welcome_seen";
const DISMISS_KEY = "texoma_welcome_dismissed";
const COLORS = ["#1A3C34", "#C06E52", "#2F5D50", "#D9A066", "#466A5E", "#A8553C", "#8A9B6E", "#C98A5E"];
const GOLD = "#E8C47C";

const SEGMENT_LABELS = {
  WELCOME10: "10% OFF",
  SAVE25: "$25 OFF",
  FREESMORES: "S'MORES KIT",
  TEXOMA15: "15% OFF",
  LATECHECKOUT: "LATE CHECKOUT",
  FREEMOVIE: "MOVIE NIGHT",
  FREENIGHT: "1 NIGHT FREE",
};

const REPEAT_TWICE = ["WELCOME10", "FREESMORES"];
const SPANS = { FREENIGHT: 0.45 };

const buildDisplaySegments = (offers) => {
  const out = [...offers];
  offers
    .filter((o) => REPEAT_TWICE.includes(o.code))
    .forEach((d) => {
      const origIdx = out.findIndex((o) => o.code === d.code);
      let pos = (origIdx + Math.floor(out.length / 2)) % (out.length + 1);
      while (out[pos - 1]?.code === d.code || out[pos]?.code === d.code) pos = (pos + 1) % (out.length + 1);
      out.splice(pos, 0, d);
    });
  const total = out.reduce((s, o) => s + (SPANS[o.code] || 1), 0);
  let cursor = 0;
  return out.map((o) => {
    const angle = ((SPANS[o.code] || 1) / total) * 360;
    const segment = { ...o, start: cursor, angle, mid: cursor + angle / 2 };
    cursor += angle;
    return segment;
  });
};

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="confetti">
    {Array.from({ length: 36 }).map((_, i) => (
      <span
        key={i}
        className="confetti-piece"
        style={{
          left: `${(i * 97) % 100}%`,
          background: [GOLD, "#C06E52", "#2F5D50", "#D9A066", "#fff"][i % 5],
          animationDelay: `${(i % 12) * 0.12}s`,
          animationDuration: `${2.2 + (i % 5) * 0.35}s`,
          width: i % 3 === 0 ? "10px" : "7px",
          height: i % 2 === 0 ? "12px" : "8px",
        }}
      />
    ))}
  </div>
);

const Wheel = ({ segments, rotation, spinning, idle }) => {
  const size = 300;
  const c = size / 2;
  const rOuter = c - 4;
  const r = c - 18;
  const bulbs = Array.from({ length: Math.max(segments.length, 7) * 2 });
  return (
    <div className={`relative mx-auto ${idle ? "wheel-idle" : ""}`} style={{ width: size, height: size, filter: "drop-shadow(0 8px 28px rgba(26,60,52,0.45))" }}>
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-2 z-10"
        style={{ width: 0, height: 0, borderLeft: "13px solid transparent", borderRight: "13px solid transparent", borderTop: `22px solid ${GOLD}`, filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.4))" }}
      />
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        data-testid="prize-wheel"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: spinning ? "transform 4.2s cubic-bezier(0.12, 0.6, 0.08, 1)" : "none",
        }}
      >
        <defs>
          <linearGradient id="rimGold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F6E2B3" />
            <stop offset="45%" stopColor={GOLD} />
            <stop offset="100%" stopColor="#B8914C" />
          </linearGradient>
          <radialGradient id="hubGold" cx="0.4" cy="0.35" r="0.9">
            <stop offset="0%" stopColor="#F6E2B3" />
            <stop offset="100%" stopColor="#B8914C" />
          </radialGradient>
          {segments.map((s, i) => (
            <radialGradient key={`${s.code}-${i}`} id={`seg${i}`} cx="0.5" cy="0.5" r="0.95">
              <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity="0.82" />
              <stop offset="70%" stopColor={COLORS[i % COLORS.length]} />
              <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity="0.92" />
            </radialGradient>
          ))}
        </defs>

        <circle cx={c} cy={c} r={rOuter} fill="url(#rimGold)" stroke="#8F6E35" strokeWidth="1.5" />
        <circle cx={c} cy={c} r={r + 2} fill="#14302A" />

        {segments.map((s, i) => {
          const start = s.start;
          const end = s.start + s.angle;
          const [x1, y1] = polar(c, c, r, start);
          const [x2, y2] = polar(c, c, r, end);
          const mid = s.mid;
          const label = SEGMENT_LABELS[s.code] || s.code;
          const narrow = s.angle < 30;
          const flip = mid > 90 && mid < 270;
          const tr = r * 0.6;
          return (
            <g key={`${s.code}-${i}`}>
              <path d={`M ${c} ${c} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`} fill={`url(#seg${i})`} stroke={GOLD} strokeWidth="1.2" />
              <text
                x={c + tr} y={c}
                fill="#FFFFFF" fontSize={narrow ? 9.5 : label.length > 11 ? 11.5 : 13.5} fontWeight="800"
                textAnchor="middle" dominantBaseline="middle"
                stroke="#14302A" strokeWidth={narrow ? "2.2" : "3"} paintOrder="stroke" strokeLinejoin="round"
                transform={`rotate(${mid - 90}, ${c}, ${c})${flip ? ` rotate(180, ${c + tr}, ${c})` : ""}`}
                style={{ letterSpacing: "0.4px" }}
              >
                {label}
              </text>
            </g>
          );
        })}

        {bulbs.map((_, i) => {
          const [bx, by] = polar(c, c, (rOuter + r + 2) / 2, (360 / bulbs.length) * i);
          return (
            <circle
              key={i}
              cx={bx} cy={by} r="3.2"
              fill="#FFF6DC"
              className={spinning ? "wheel-bulb-fast" : "wheel-bulb"}
              style={{ animationDelay: `${(i % 2) * 0.5}s` }}
            />
          );
        })}

        <circle cx={c} cy={c} r="27" fill="url(#hubGold)" stroke="#8F6E35" strokeWidth="1.5" />
        <circle cx={c} cy={c} r="19" fill="#14302A" />
        <text x={c} y={c + 1} fill={GOLD} fontSize="10.5" fontWeight="700" textAnchor="middle" dominantBaseline="middle" style={{ letterSpacing: "1px" }}>SPIN</text>
      </svg>
    </div>
  );
};

export const WelcomePopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [phase, setPhase] = useState("form");
  const [rotation, setRotation] = useState(0);
  const [offer, setOffer] = useState(null);
  const pendingOffer = useRef(null);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY) || sessionStorage.getItem(DISMISS_KEY)) return undefined;
    const timer = setTimeout(() => {
      fetch(`${API}/api/wheel/segments`)
        .then((r) => (r.ok ? r.json() : []))
        .then((segs) => {
          if (segs.length >= 2) {
            setSegments(buildDisplaySegments(segs));
            setOpen(true);
          }
        })
        .catch(() => {});
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  if (pathname.startsWith("/admin") || pathname.startsWith("/payment-success")) return null;

  const dismiss = (value) => {
    if (!value && phase !== "spinning") {
      sessionStorage.setItem(DISMISS_KEY, "1");
      setOpen(false);
    }
  };

  const spin = async () => {
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast.error("Please enter a valid email to spin");
      return;
    }
    setPhase("submitting");
    try {
      const res = await fetch(`${API}/api/wheel/spin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Something went wrong. Please try again.");
        setPhase("form");
        return;
      }
      localStorage.setItem(SEEN_KEY, "1");
      pendingOffer.current = data.offer;
      const matches = segments.filter((s) => s.code === data.offer.code);
      const landed = matches[Math.floor(Math.random() * matches.length)] || segments[0];
      const target = 360 * 5 - landed.mid;
      setPhase("spinning");
      requestAnimationFrame(() => setRotation(target));
      setTimeout(() => {
        setOffer(pendingOffer.current);
        setPhase("won");
      }, 4500);
    } catch {
      toast.error("Something went wrong. Please try again.");
      setPhase("form");
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(offer.code);
    toast.success("Code copied! Paste it in your cart at checkout.");
  };

  return (
    <Dialog open={open} onOpenChange={dismiss}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 max-h-[92vh] overflow-y-auto bg-transparent shadow-none" data-testid="welcome-popup">
        <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(160deg, #16352E 0%, #1A3C34 55%, #0F2621 100%)", border: `1px solid ${GOLD}55` }}>
          {phase === "won" && <Confetti />}
          <div className="px-7 pt-7 pb-4 text-center relative">
            <p className="font-body text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>
              Serenity Village
            </p>
            <DialogTitle className="font-heading text-3xl font-light text-white tracking-tight">
              {phase === "won" ? "You Won!" : "Spin to Win"}
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-white/60 mt-1.5">
              {phase === "won"
                ? offer?.title
                : "Everyone wins — from 10% off up to ONE NIGHT FREE (3+ night stays)"}
            </DialogDescription>
          </div>
          <div className="px-6 pb-7 relative">
            {phase === "won" ? (
              <div className="text-center pt-2" data-testid="welcome-popup-code-view">
                <button
                  data-testid="welcome-popup-code"
                  onClick={copyCode}
                  className="inline-flex items-center gap-2 rounded-xl px-7 py-3.5 font-heading text-2xl tracking-[0.18em] transition-transform hover:scale-105"
                  style={{ border: `2px dashed ${GOLD}`, color: GOLD, background: "rgba(232,196,124,0.08)" }}
                >
                  {offer.code} <Copy size={16} />
                </button>
                <p className="font-body text-sm text-white/60 mt-4 max-w-xs mx-auto">{offer.description}</p>
                <Button
                  data-testid="welcome-popup-done"
                  onClick={() => setOpen(false)}
                  className="mt-6 w-full bg-accent hover:bg-accent/90 text-white font-body rounded-full h-11"
                >
                  Use My Prize
                </Button>
                <p className="font-body text-[11px] text-white/35 mt-3">We also emailed this code to you</p>
              </div>
            ) : (
              <div className="space-y-5">
                <Wheel segments={segments} rotation={rotation} spinning={phase === "spinning"} idle={phase === "form"} />
                {phase === "spinning" ? (
                  <p data-testid="wheel-spinning-text" className="font-body text-sm text-center animate-pulse" style={{ color: GOLD }}>
                    Spinning... good luck! 🍀
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid="welcome-popup-name" placeholder="First name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="font-body bg-white/95 border-0 h-11" />
                      <Input data-testid="welcome-popup-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onKeyDown={(e) => e.key === "Enter" && spin()} className="font-body bg-white/95 border-0 h-11" />
                    </div>
                    <Button
                      data-testid="welcome-popup-submit"
                      onClick={spin}
                      disabled={phase === "submitting"}
                      className="w-full text-[#14302A] font-body font-semibold rounded-full h-12 text-base hover:opacity-90 transition-opacity"
                      style={{ background: `linear-gradient(135deg, #F6E2B3, ${GOLD} 55%, #D9A95E)` }}
                    >
                      {phase === "submitting" ? <Loader2 size={16} className="animate-spin" /> : "✨ Spin the Wheel"}
                    </Button>
                    <button
                      data-testid="welcome-popup-dismiss"
                      onClick={() => dismiss(false)}
                      className="w-full font-body text-xs text-white/40 hover:text-white/70 transition-colors flex items-center justify-center gap-1"
                    >
                      <X size={12} /> No thanks, maybe later
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
